import React from 'react';
import PropTypes from 'prop-types';

/**
 * Divider Component
 * 
 * A horizontal divider line for separating content.
 * Supports optional labels, different styles, and spacing control.
 * 
 * @example
 * // Basic divider
 * <Divider />
 * 
 * @example
 * // Divider with label
 * <Divider label="OR" />
 * 
 * @example
 * // Dashed divider with label
 * <Divider label="Section 2" variant="dashed" spacing="lg" />
 */
const Divider = ({
  label,
  variant = 'solid',
  spacing = 'md',
  labelPosition = 'center',
  className = '',
  ...props
}) => {
  // Variant classes for the line
  const variantClasses = {
    solid: 'border-solid',
    dashed: 'border-dashed',
    dotted: 'border-dotted',
  };
  
  // Spacing classes (vertical margin)
  const spacingClasses = {
    none: 'my-0',
    sm: 'my-2',
    md: 'my-4',
    lg: 'my-6',
    xl: 'my-8',
  };
  
  // Label position classes
  const labelPositionClasses = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
  };
  
  // If there's a label, render divider with label
  if (label) {
    return (
      <div
        className={`relative flex items-center ${spacingClasses[spacing]} ${className}`}
        {...props}
      >
        <div
          className={`flex-1 flex ${labelPositionClasses[labelPosition]}`}
        >
          {labelPosition !== 'left' && (
            <div className={`flex-1 border-t border-gray-300 ${variantClasses[variant]} ${labelPosition === 'center' ? 'mr-3' : 'mr-0'}`} />
          )}
          
          <span className="px-3 text-sm font-medium text-gray-500 whitespace-nowrap">
            {label}
          </span>
          
          {labelPosition !== 'right' && (
            <div className={`flex-1 border-t border-gray-300 ${variantClasses[variant]} ${labelPosition === 'center' ? 'ml-3' : 'ml-0'}`} />
          )}
        </div>
      </div>
    );
  }
  
  // Simple divider without label
  return (
    <hr
      className={`border-t border-gray-300 ${variantClasses[variant]} ${spacingClasses[spacing]} ${className}`}
      {...props}
    />
  );
};

Divider.propTypes = {
  /** Optional label text */
  label: PropTypes.string,
  /** Line style variant */
  variant: PropTypes.oneOf(['solid', 'dashed', 'dotted']),
  /** Vertical spacing (margin) */
  spacing: PropTypes.oneOf(['none', 'sm', 'md', 'lg', 'xl']),
  /** Label position (only applies when label is provided) */
  labelPosition: PropTypes.oneOf(['left', 'center', 'right']),
  /** Additional CSS classes */
  className: PropTypes.string,
};

export default Divider;
